import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import NoiRockLogo from '../../components/NoiRockLogo';

const TermsOfService = () => {
  const tenant = useTenant();
  const isTcn = (tenant?.effectiveTenantSlug ?? tenant?.tenantSlug) === 'tcn';
  const brandName = isTcn ? 'TCN Agent' : 'NoiRock';
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, var(--color-primary) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Header — tenant-aware: TCN logo + name on tcn.noirock.app, NoiRock otherwise.
          Back arrow + brand both route to /login-authentication so the user returns
          to the right tenant's login screen. */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <Link to="/login-authentication" aria-label="Back to login" className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 no-underline hover:bg-white/10 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </Link>
          <Link to="/login-authentication" className="flex items-center gap-2 no-underline">
            <NoiRockLogo size={28} />
            <span className="text-white/60 text-xs font-semibold tracking-wide">{brandName}</span>
          </Link>
        </div>
        <Link
          to="/privacy"
          className="text-white/40 text-xs hover:text-white/70 transition-colors no-underline"
        >
          Privacy Policy →
        </Link>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div className="mb-10">
            <h1
              className="text-2xl font-bold text-white tracking-tight mb-2"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Terms of Service
            </h1>
            <p className="text-white/30 text-sm">Last updated: March 20, 2026</p>
          </div>

          <div className="space-y-8 text-white/60 text-sm leading-relaxed">

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                1. About NoiRock
              </h2>
              <p>
                NoiRock is a proprietary agency management platform operated by NoiRock 
                (hereinafter "the Agency"), accessible at noirock.app. The platform is designed 
                for TikTok LIVE creators and managers affiliated with the Agency to track performance, 
                manage operations, and access agency resources.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                2. Acceptance of Terms
              </h2>
              <p>
                By accessing or using NoiRock, including authentication via TikTok Login Kit, you agree 
                to be bound by these Terms of Service. If you do not agree with any part of these terms, 
                you must not use the platform.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                3. Access & Eligibility
              </h2>
              <p>
                Access to NoiRock is restricted to creators and managers registered in the Agency's 
                database. Authentication is performed via TikTok Login Kit for creators and email/password 
                for managers and administrators. The Agency reserves the right to grant, suspend, or revoke 
                access at its sole discretion without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                4. TikTok Authentication
              </h2>
              <p>
                When you authenticate via TikTok Login Kit, you authorize the Agency to access the 
                following information from your TikTok account:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li>Your TikTok unique identifiers (open_id, union_id)</li>
                <li>Your display name and profile avatar</li>
                <li>Your TikTok username</li>
              </ul>
              <p className="mt-2">
                This data is used solely for identity verification and platform personalization. 
                You may revoke this access at any time through your TikTok account settings.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                5. User Obligations
              </h2>
              <p>You agree to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-white/50">
                <li>Not share your access credentials or sessions with any third party</li>
                <li>Use the platform in accordance with its intended purpose</li>
                <li>Not attempt to circumvent any security measures or access restrictions</li>
                <li>Not extract, scrape, or redistribute data from the platform</li>
                <li>Comply with TikTok's Terms of Service in all related activities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                6. Intellectual Property
              </h2>
              <p>
                NoiRock, including its design, source code, branding, and all associated content, is 
                the exclusive property of NoiRock. No license or right is granted beyond the 
                limited right to use the platform as an authorized user.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                7. Data & Performance Information
              </h2>
              <p>
                Performance data displayed on the platform (diamonds, valid days, rankings) is sourced 
                from TikTok Backstage and internal Agency records. While the Agency strives for accuracy, 
                it does not guarantee the completeness or real-time accuracy of this data. Official 
                figures from TikTok Backstage take precedence in case of discrepancy.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                8. Limitation of Liability
              </h2>
              <p>
                The platform is provided "as is" without warranty of any kind. The Agency shall not be 
                liable for any direct, indirect, incidental, or consequential damages arising from the 
                use or inability to use the platform, including but not limited to data loss, service 
                interruptions, or inaccurate performance data.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                9. Modifications
              </h2>
              <p>
                The Agency reserves the right to modify these Terms of Service at any time. Continued 
                use of the platform after any modification constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-white/90 text-base font-semibold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                10. Contact
              </h2>
              <p>
                For any questions regarding these terms, please contact the Agency at:{' '}
                <span className="text-white/80">contact@noirock.app</span>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-6 border-t border-white/5 text-center">
            <p className="text-white/20 text-xs">
              © 2026 {brandName} · v2.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
